import { sessionRequests } from '@/lib/auth/sessionRequests';
import type { Json } from '@/types/database';
import type { GameAdEvent } from '../types';

type GameAdEventResult = { accepted?: boolean; duplicate?: boolean; reason?: string };

const EVENT_RPC_TIMEOUT_MS = 2_500;
const EVENT_RETRY_DELAY_MS = 200;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function recordOnce(event: GameAdEvent, assertOwner: () => void): Promise<GameAdEventResult> {
  let timedOut = false;
  const request = (async () => {
    const { supabase } = await import('@/lib/supabase');
    assertOwner();
    if (timedOut) throw new Error('Game ad telemetry timed out');
    return supabase.rpc('record_game_ad_event', { p_event: event as Json });
  })();
  let timeoutId: number | undefined;
  try {
    const response = await Promise.race([
      request,
      new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(() => {
          timedOut = true;
          reject(new Error('Game ad telemetry timed out'));
        }, EVENT_RPC_TIMEOUT_MS);
      }),
    ]);
    if (response.error) throw response.error;
    return (response.data as GameAdEventResult | null) ?? {};
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

/** Bounded idempotent retry; callers never await this on a gameplay transition. */
export async function recordGameAdEvent(event: GameAdEvent): Promise<void> {
  const assertOwner = sessionRequests.captureOwner();
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const result = await recordOnce(event, assertOwner);
      if (result.reason === 'rate_limited' || result.accepted === false) return;
      return;
    } catch {
      if (attempt === 0) await delay(EVENT_RETRY_DELAY_MS);
    }
  }
}
